import { useCallback, useMemo } from 'react';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import {
  useGetEncounterEligibilityQuery,
  useGetPatientEligibilityQuery,
  useRunEligibilityCheckMutation,
  useRunPatientEligibilityCheckMutation,
} from '../store/api';
import type { EligibilityCheckResponse } from '../types';

export type EligibilityCardState = 'hidden' | 'needs_check' | 'check_complete';

interface UseEligibilityCardStateOptions {
  patientId?: string | null;
  encounterId?: string | null;
}

interface UseEligibilityCardStateResult {
  state: EligibilityCardState;
  eligibilityData: EligibilityCheckResponse | undefined;
  isLoading: boolean;
  runCheck: () => void;
  isRunning: boolean;
}

export function useEligibilityCardState({
  patientId,
  encounterId,
}: UseEligibilityCardStateOptions): UseEligibilityCardStateResult {
  // Check feature gate
  const isEnabled = useFeatureGate(FeatureGates.ELIGIBILITY_CHECKS);

  // Encounter-scoped query: used when encounterId is available
  const {
    data: encounterEligibilityData,
    isLoading: isEncounterLoading,
  } = useGetEncounterEligibilityQuery(
    { encounterId: encounterId ?? '' },
    { skip: !isEnabled || !encounterId },
  );

  // Patient-scoped query: used when only patientId is available (no encounterId)
  const {
    data: patientEligibilityData,
    isLoading: isPatientLoading,
  } = useGetPatientEligibilityQuery(
    { patientId: patientId ?? '' },
    { skip: !isEnabled || !!encounterId || !patientId },
  );

  // Encounter-scoped mutation
  const [triggerEncounterCheck, { isLoading: isEncounterRunning }] =
    useRunEligibilityCheckMutation();

  // Patient-scoped mutation
  const [triggerPatientCheck, { isLoading: isPatientRunning }] =
    useRunPatientEligibilityCheckMutation();

  const runCheck = useCallback(() => {
    if (encounterId) {
      triggerEncounterCheck({ encounterId });
    } else if (patientId) {
      triggerPatientCheck({ patientId });
    }
  }, [encounterId, patientId, triggerEncounterCheck, triggerPatientCheck]);

  // Prefer encounter-scoped data, fall back to patient-scoped
  const eligibilityData = encounterEligibilityData ?? patientEligibilityData;

  const state = useMemo<EligibilityCardState>(() => {
    if (!isEnabled) return 'hidden';
    if (!patientId && !encounterId) return 'hidden';
    if (eligibilityData) return 'check_complete';
    return 'needs_check';
  }, [isEnabled, patientId, encounterId, eligibilityData]);

  const isLoading =
    isEnabled &&
    ((!!encounterId && isEncounterLoading) ||
      (!encounterId && !!patientId && isPatientLoading));

  const isRunning = isEncounterRunning || isPatientRunning;

  return {
    state,
    eligibilityData,
    isLoading,
    runCheck,
    isRunning,
  };
}
